import { Link, useLocation, useNavigate } from "react-router-dom";
import { Facebook, Linkedin } from "lucide-react";
import { SERVICES } from "@/data/services";
import { scrollToId } from "@/lib/motion";

const STUDIO_LINKS = [
  { label: "About", route: "/about" },
  { label: "Packages", route: "/packages" },
  { label: "Todd", route: "/todd" },
  { label: "FAQ", route: "/faq" },
  { label: "Testimonials", route: "/testimonials" },
  { label: "Case Studies", route: "/case-studies" },
  { id: "results", label: "Results" },
  { label: "Book a Call", route: "/free-strategy-call" },
  { label: "Contact", route: "/contact" },
];

export default function Footer() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const year = new Date().getFullYear();

  const goSection = (id) => () => {
    if (pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
    } else {
      scrollToId(id);
    }
  };

  return (
    <footer
      data-testid="main-footer"
      className="relative overflow-hidden bg-[#0B0F19] text-slate-100"
    >
      <div className="mx-auto max-w-7xl px-4 pt-20 sm:px-8 lg:px-12">
        <div className="grid gap-12 pb-16 sm:grid-cols-2 md:grid-cols-3">
          <div>
            <img
              src="/img/logo-300.png"
              alt="ZD Design"
              className="w-[208px] h-auto"
            />
            <p className="mt-5 max-w-xs text-base leading-relaxed text-slate-400">
              We fix the gaps in your website, SEO, and advertising — turning
              your online presence into more calls, leads, and customers.
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                href="https://www.linkedin.com/in/zddesign/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin-link"
                aria-label="ZD Design on LinkedIn"
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/25 text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-flame/50 hover:text-flame"
              >
                <Linkedin className="h-4 w-4" />
              </a>
              <a
                href="https://www.facebook.com/ZDdesign"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-facebook-link"
                aria-label="ZD Design on Facebook"
                className="inline-flex h-10 w-10 items-center justify-center rounded-2xl border border-white/25 text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-flame/50 hover:text-flame"
              >
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div>
            <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-slate-500">
              Services
            </div>
            <ul className="mt-5 space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={s.route || `/${s.slug}`}
                    data-testid={`footer-service-link-${s.slug}`}
                    className="text-base text-slate-400 transition-colors hover:text-flame"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <div className="font-mono text-[13px] uppercase tracking-[0.25em] text-slate-500">
              Studio
            </div>
            <ul className="mt-5 space-y-3">
              {STUDIO_LINKS.map((l) => (
                <li key={l.id || l.label}>
                  {l.route ? (
                    <Link
                      to={l.route}
                      data-testid={`footer-studio-link-${l.id || l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-base text-slate-400 transition-colors hover:text-flame"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <button
                      data-testid={`footer-studio-link-${l.id}`}
                      onClick={goSection(l.id)}
                      className="text-base text-slate-400 transition-colors hover:text-flame"
                    >
                      {l.label}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          aria-hidden="true"
          className="select-none whitespace-nowrap text-center font-display text-[17vw] font-extrabold leading-[0.8] tracking-tight text-slate-100/[0.045]"
        >
          ZD·DESIGN
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 py-8 font-mono text-[13px] uppercase tracking-[0.18em] text-slate-500 sm:flex-row">
          <span data-testid="footer-copyright">
            © {year} ZD Design. All rights reserved.
          </span>
          <Link
            to="/ai-visibility"
            data-testid="footer-ai-visibility-link"
            className="font-semibold text-gold transition-colors hover:text-flame"
          >
            AI Visibility — $397/mo
          </Link>
          <Link
            to="/sitemap"
            data-testid="footer-sitemap-link"
            className="transition-colors hover:text-flame"
          >
            Sitemap
          </Link>
          <Link
            to="/privacy"
            data-testid="footer-privacy-link"
            className="transition-colors hover:text-flame"
          >
            Privacy
          </Link>
          <span>Kansas City — clients nationwide</span>
          <span className="text-flame">SEO · Remarketing · Optimization</span>
        </div>
      </div>
    </footer>
  );
}
